import React, { useState, useEffect, useMemo } from 'react';
import {
  useEncomendas,
  useCriarEncomenda,
  useAtualizarEncomenda,
  useRemoverEncomenda,
  useAlterarStatusEncomenda,
  useEncomenda,
} from '../../hooks/useEncomendas';
import { useClientes } from '../../hooks/useClientes';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useProdutos } from '../../hooks/useProdutos';
import { StatusEncomenda } from '../../types';
import ConfirmModal from '../../components/Common/ConfirmModal';
import SearchBar from '../../components/Common/SearchBar';
import { aplicarMascaraTelefone, formatarTelefone } from '../../utils/formatters';
import Select from '../../components/Common/Select';
import Input from '../../components/Common/Input';
import { validateRequired, validateFutureDate, validateNonNegativeNumber, combineValidations } from '../../utils/validations';
import toast from 'react-hot-toast';

const Encomendas: React.FC = () => {
  usePageTitle('Encomendas');
  const { user } = useAuth();
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [filtroPeriodo, setFiltroPeriodo] = useState<string>('pendentes');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [termoBusca, setTermoBusca] = useState('');

  const calcularDatas = () => {
    const hoje = new Date();
    const hojeStr = hoje.toISOString().split('T')[0];

    switch (filtroPeriodo) {
      case 'pendentes':
        return { dataInicio: hojeStr, dataFim: undefined };
      case 'hoje':
        return { dataInicio: hojeStr, dataFim: hojeStr };
      case 'semana': {
        const inicioSemana = new Date(hoje);
        inicioSemana.setDate(hoje.getDate() - hoje.getDay());
        const fimSemana = new Date(inicioSemana);
        fimSemana.setDate(inicioSemana.getDate() + 6);
        return {
          dataInicio: inicioSemana.toISOString().split('T')[0],
          dataFim: fimSemana.toISOString().split('T')[0],
        };
      }
      case 'mes': {
        const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return {
          dataInicio: inicioMes.toISOString().split('T')[0],
          dataFim: fimMes.toISOString().split('T')[0],
        };
      }
      default: // 'todas'
        return { dataInicio: undefined, dataFim: undefined };
    }
  };

  const { dataInicio, dataFim } = calcularDatas();

  const [modalAberto, setModalAberto] = useState(false);
  const [encomendaId, setEncomendaId] = useState<number | null>(null);
  const [confirmarRemocao, setConfirmarRemocao] = useState<{
    isOpen: boolean;
    encomendaId: number | null;
    encomendaNome: string;
  }>({ isOpen: false, encomendaId: null, encomendaNome: '' });

  const [confirmarStatus, setConfirmarStatus] = useState<{
    isOpen: boolean;
    encomendaId: number | null;
    novoStatus: StatusEncomenda | null;
    statusAtual: StatusEncomenda | null;
  }>({ isOpen: false, encomendaId: null, novoStatus: null, statusAtual: null });

  const [formData, setFormData] = useState<any>({
    clienteId: null as number | null,
    nomeCliente: '',
    contatoCliente: '',
    estabelecimentoId: user?.estabelecimentoId || 1,
    dataHoraEntrega: '',
    retirada: false,
    enderecoEntrega: '',
    observacoes: '',
    desconto: '',
    valorEntrada: '',
    entradaPaga: false,
    itens: [],
  });

  useEffect(() => {
    if (user?.estabelecimentoId) {
      setFormData((prev: any) => ({
        ...prev,
        estabelecimentoId: user.estabelecimentoId!,
      }));
    }
  }, [user]);

  const { data: encomendaData, isLoading, isError, error } = useEncomendas({
    pagina: paginaAtual,
    tamanhoPagina: 10,
    dataInicio,
    dataFim,
    estabelecimentoId: user?.estabelecimentoId,
  });

  const { data: encomendaDetalhes } = useEncomenda(encomendaId || 0);
  const { data: clientes } = useClientes();
  const { data: produtos } = useProdutos(true);
  const criarEncomendaMutation = useCriarEncomenda();
  const atualizarEncomendaMutation = useAtualizarEncomenda();
  const removerEncomendaMutation = useRemoverEncomenda();
  const alterarStatusMutation = useAlterarStatusEncomenda();

  const encomendas = useMemo(() => encomendaData?.encomendas || [], [encomendaData]);
  const totalPaginas = encomendaData?.totalPaginas || 1;
  const totalEncomendas = encomendaData?.total || 0;

  const encomendasFiltradas = useMemo(() => {
    let resultado = encomendas;

    if (filtroStatus !== 'todos') {
      resultado = resultado.filter(e => e.status === filtroStatus);
    }

    if (termoBusca.trim()) {
      const termo = termoBusca.toLowerCase();
      resultado = resultado.filter(e =>
        e.nomeCliente.toLowerCase().includes(termo) ||
        e.contatoCliente?.toLowerCase().includes(termo)
      );
    }

    return resultado;
  }, [encomendas, filtroStatus, termoBusca]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const isDentroDeUmaSemana = (dataHoraEntrega: string) => {
    const dataEntrega = new Date(dataHoraEntrega);
    const hoje = new Date();
    const umaSemana = new Date();
    umaSemana.setDate(hoje.getDate() + 7);
    return dataEntrega >= hoje && dataEntrega <= umaSemana;
  };

  const resetFormData = () => ({
    clienteId: null as number | null,
    nomeCliente: '',
    contatoCliente: '',
    estabelecimentoId: user?.estabelecimentoId || 1,
    dataHoraEntrega: '',
    retirada: false,
    enderecoEntrega: '',
    observacoes: '',
    desconto: '',
    valorEntrada: '',
    entradaPaga: false,
    itens: [],
  });

  const fecharModal = () => {
    setModalAberto(false);
    setEncomendaId(null);
    setFormData(resetFormData());
  };

  // Preenche o formulário quando os detalhes da encomenda são carregados
  useEffect(() => {
    if (encomendaDetalhes && encomendaId) {
      setFormData({
        clienteId: encomendaDetalhes.clienteId || null,
        nomeCliente: encomendaDetalhes.nomeCliente || '',
        contatoCliente: encomendaDetalhes.contatoCliente || '',
        estabelecimentoId: user?.estabelecimentoId || 1,
        dataHoraEntrega: new Date(encomendaDetalhes.dataHoraEntrega).toISOString().slice(0, 16),
        retirada: encomendaDetalhes.retirada,
        enderecoEntrega: encomendaDetalhes.enderecoEntrega || '',
        observacoes: encomendaDetalhes.observacoes || '',
        desconto: String(encomendaDetalhes.desconto || ''),
        valorEntrada: String(encomendaDetalhes.valorEntrada || ''),
        entradaPaga: encomendaDetalhes.entradaPaga || false,
        itens: encomendaDetalhes.itens.map((item: any) => ({
          produtoId: item.produtoId,
          quantidade: item.quantidade,
        })),
      });
    }
  }, [encomendaDetalhes, encomendaId, user]);

  const abrirModal = async (id?: number) => {
    if (id) {
      setEncomendaId(id);
    } else {
      setEncomendaId(null);
      setFormData(resetFormData());
    }
    setModalAberto(true);
  };

  const handleClienteChange = (clienteIdStr: string) => {
    const clienteId = parseInt(clienteIdStr);
    if (clienteId > 0) {
      const cliente = clientes?.find((c) => c.id === clienteId);
      if (cliente) {
        setFormData({
          ...formData,
          clienteId,
          nomeCliente: cliente.nome,
          contatoCliente: cliente.telefone || '',
        });
      }
    } else {
      setFormData({
        ...formData,
        clienteId: null,
        nomeCliente: '',
        contatoCliente: '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nomeCliente.trim()) {
      toast.error('Informe o nome do cliente', { duration: 3000, position: 'top-center' });
      return;
    }

    if (formData.itens.length === 0 && !encomendaId) {
      toast.error('Adicione pelo menos um produto à encomenda', { duration: 3000, position: 'top-center' });
      return;
    }

    try {
      const desconto = parseFloat(String(formData.desconto).replace(',', '.')) || 0;
      const valorEntrada = parseFloat(String(formData.valorEntrada).replace(',', '.')) || 0;

      if (encomendaId) {
        const itensComDados = formData.itens.map((item: any) => {
          const produto = produtos?.find((p) => p.id === item.produtoId);
          if (!produto) {
            throw new Error(`Produto ${item.produtoId} não encontrado`);
          }
          return {
            produtoId: item.produtoId,
            nomeProduto: produto.nome,
            precoUnitario: produto.precoUnitario,
            quantidade: item.quantidade,
          };
        });

        await atualizarEncomendaMutation.mutateAsync({
          id: encomendaId,
          data: {
            nomeCliente: formData.nomeCliente,
            contatoCliente: formData.contatoCliente || undefined,
            dataHoraEntrega: formData.dataHoraEntrega,
            retirada: formData.retirada,
            enderecoEntrega: formData.enderecoEntrega,
            observacoes: formData.observacoes,
            desconto,
            valorEntrada,
            entradaPaga: formData.entradaPaga,
            itens: itensComDados,
          },
        });
      } else {
        await criarEncomendaMutation.mutateAsync({
          nomeCliente: formData.nomeCliente,
          contatoCliente: formData.contatoCliente || undefined,
          clienteId: formData.clienteId || undefined,
          estabelecimentoId: formData.estabelecimentoId,
          dataHoraEntrega: formData.dataHoraEntrega,
          retirada: formData.retirada,
          enderecoEntrega: formData.enderecoEntrega,
          observacoes: formData.observacoes,
          desconto,
          valorEntrada,
          entradaPaga: formData.entradaPaga,
          itens: formData.itens,
        });
      }
      fecharModal();
    } catch (error) {
      console.error('Erro ao salvar encomenda:', error);
    }
  };

  const adicionarProduto = () => {
    const produtoId = parseInt((document.getElementById('produtoSelect') as HTMLSelectElement).value);
    if (produtoId === 0) return;

    const produtoExistente = formData.itens.find((item: any) => item.produtoId === produtoId);
    if (produtoExistente) {
      toast.error('Produto já adicionado à encomenda', { duration: 3000, position: 'top-center' });
      return;
    }

    setFormData({
      ...formData,
      itens: [...formData.itens, { produtoId, quantidade: 1 }],
    });
  };

  const removerProduto = (produtoId: number) => {
    setFormData({
      ...formData,
      itens: formData.itens.filter((item: any) => item.produtoId !== produtoId),
    });
  };

  const atualizarQuantidade = (produtoId: number, quantidade: number) => {
    if (quantidade < 1) return;
    setFormData({
      ...formData,
      itens: formData.itens.map((item: any) =>
        item.produtoId === produtoId ? { ...item, quantidade } : item
      ),
    });
  };

  const calcularTotal = () => {
    const subtotal = formData.itens.reduce((total: number, item: any) => {
      const produto = produtos?.find((p) => p.id === item.produtoId);
      return total + (produto?.precoUnitario || 0) * item.quantidade;
    }, 0);
    const descontoNum = parseFloat(String(formData.desconto).replace(',', '.')) || 0;
    return subtotal - descontoNum;
  };

  const confirmarRemover = (id: number, nomeCliente: string) => {
    setConfirmarRemocao({ isOpen: true, encomendaId: id, encomendaNome: nomeCliente });
  };

  const handleConfirmarRemocao = async () => {
    if (confirmarRemocao.encomendaId) {
      await removerEncomendaMutation.mutateAsync(confirmarRemocao.encomendaId);
      fecharConfirmacaoRemocao();
    }
  };

  const fecharConfirmacaoRemocao = () => {
    setConfirmarRemocao({ isOpen: false, encomendaId: null, encomendaNome: '' });
  };

  const solicitarMudancaStatus = (encomendaId: number, statusAtual: StatusEncomenda, novoStatus: StatusEncomenda) => {
    setConfirmarStatus({ isOpen: true, encomendaId, novoStatus, statusAtual });
  };

  const handleConfirmarMudancaStatus = async () => {
    if (confirmarStatus.encomendaId && confirmarStatus.novoStatus) {
      await alterarStatusMutation.mutateAsync({
        id: confirmarStatus.encomendaId,
        status: confirmarStatus.novoStatus,
      });
      fecharConfirmacaoStatus();
    }
  };

  const fecharConfirmacaoStatus = () => {
    setConfirmarStatus({ isOpen: false, encomendaId: null, novoStatus: null, statusAtual: null });
  };

  const getProximoStatus = (statusAtual: StatusEncomenda): StatusEncomenda | null => {
    const transicoes: { [key in StatusEncomenda]?: StatusEncomenda } = {
      [StatusEncomenda.Aguardando]: StatusEncomenda.EmProducao,
      [StatusEncomenda.EmProducao]: StatusEncomenda.Pronto,
      [StatusEncomenda.Pronto]: StatusEncomenda.Entregue,
    };
    return transicoes[statusAtual] || null;
  };

  const getStatusLabel = (status: StatusEncomenda): string => {
    const labels: { [key in StatusEncomenda]: string } = {
      [StatusEncomenda.Aguardando]: 'Aguardando',
      [StatusEncomenda.EmProducao]: 'Em Produção',
      [StatusEncomenda.Pronto]: 'Pronto',
      [StatusEncomenda.Entregue]: 'Entregue',
    };
    return labels[status];
  };

  const getStatusBadge = (status: StatusEncomenda) => {
    const classes = {
      [StatusEncomenda.Aguardando]: 'bg-yellow-100 text-yellow-800',
      [StatusEncomenda.EmProducao]: 'bg-blue-100 text-blue-800',
      [StatusEncomenda.Pronto]: 'bg-green-100 text-green-800',
      [StatusEncomenda.Entregue]: 'bg-gray-100 text-neutral-900',
    };

    const labels = {
      [StatusEncomenda.Aguardando]: 'Aguardando',
      [StatusEncomenda.EmProducao]: 'Em Produção',
      [StatusEncomenda.Pronto]: 'Pronto',
      [StatusEncomenda.Entregue]: 'Entregue',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-neutral-600">Carregando encomendas...</div>
      </div>
    );
  }

  if (isError) {
    const errorMessage = (error as any)?.response?.data?.error
      || (error as any)?.message
      || 'Erro desconhecido ao carregar encomendas';
    return (
      <div className="container mx-auto px-4 py-8 animate-fadeIn">
        <h1 className="text-4xl font-display font-bold text-neutral-900 mb-6">Encomendas</h1>
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-6 py-4 rounded-lg">
          <p className="font-medium">Erro ao carregar encomendas</p>
          <p className="text-sm mt-1">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 animate-fadeIn">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl font-display font-bold text-neutral-900">Encomendas</h1>
        <button
          onClick={() => abrirModal()}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Nova Encomenda
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-neutral-50 p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1 sm:max-w-sm">
            <SearchBar
              value={termoBusca}
              onChange={setTermoBusca}
              placeholder="Buscar por cliente..."
            />
          </div>

          <div className="w-full sm:w-48">
            <Select
              label="Status"
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="Aguardando">Aguardando</option>
              <option value="EmProducao">Em Produção</option>
              <option value="Pronto">Pronto</option>
              <option value="Entregue">Entregue</option>
            </Select>
          </div>

          <div className="w-full sm:w-48">
            <Select
              label="Período"
              value={filtroPeriodo}
              onChange={(e) => { setFiltroPeriodo(e.target.value); setPaginaAtual(1); }}
            >
              <option value="pendentes">Futuras</option>
              <option value="hoje">Hoje</option>
              <option value="semana">Esta semana</option>
              <option value="mes">Este mês</option>
              <option value="todas">Todas</option>
            </Select>
          </div>
        </div>

        {!isLoading && (
          <div className="mt-3 text-sm text-neutral-600">
            <span className="font-medium">
              {encomendasFiltradas.length === 0
                ? 'Nenhuma encomenda encontrada'
                : encomendasFiltradas.length < totalEncomendas
                ? `${encomendasFiltradas.length} de ${totalEncomendas} encomendas`
                : totalEncomendas === 1
                ? '1 encomenda encontrada'
                : `${totalEncomendas} encomendas encontradas`}
            </span>
            {totalPaginas > 1 && (
              <span className="ml-2 text-neutral-500">
                (Página {paginaAtual} de {totalPaginas})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                Data/Hora Entrega
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase hidden sm:table-cell">
                Tipo/Endereço
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase hidden sm:table-cell">
                Produtos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                Valor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {encomendasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-neutral-600 font-medium">Nenhuma encomenda encontrada</p>
                    <p className="text-sm text-neutral-500">
                      {termoBusca || filtroStatus !== 'todos'
                        ? 'Tente ajustar os filtros.'
                        : 'Clique em "Nova Encomenda" para começar.'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              encomendasFiltradas.map((encomenda) => (
                <tr
                  key={encomenda.id}
                  className={
                    isDentroDeUmaSemana(encomenda.dataHoraEntrega)
                      ? 'bg-yellow-50 hover:bg-yellow-100'
                      : 'hover:bg-neutral-50'
                  }
                >
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>{encomenda.nomeCliente}</div>
                    {encomenda.contatoCliente && (
                      <div className="text-xs text-neutral-500 mt-0.5">{formatarTelefone(encomenda.contatoCliente)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatDate(encomenda.dataHoraEntrega)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 hidden sm:table-cell">
                    {encomenda.retirada ? (
                      <span className="text-primary-600 font-medium">Retirada</span>
                    ) : (
                      <div>
                        <div className="text-success-600 font-medium">Entrega</div>
                        {encomenda.enderecoEntrega && (
                          <div className="text-xs text-neutral-500 mt-1">{encomenda.enderecoEntrega}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600 hidden sm:table-cell">
                    {encomenda.itens && encomenda.itens.length > 0 ? (
                      <div className="space-y-1">
                        {encomenda.itens.map((item, index) => (
                          <div key={index} className="text-xs">
                            {item.quantidade}x {item.produtoNome}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(encomenda.status as any)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatCurrency(encomenda.valorTotal)}
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    {(() => {
                      const proximo = getProximoStatus(encomenda.status as StatusEncomenda);
                      return proximo ? (
                        <button
                          onClick={() =>
                            solicitarMudancaStatus(
                              encomenda.id,
                              encomenda.status as StatusEncomenda,
                              proximo
                            )
                          }
                          className="text-xs bg-primary-50 text-primary-700 border border-primary-200 px-3 py-1.5 rounded-md hover:bg-primary-100 transition-colors font-medium"
                        >
                          {getStatusLabel(proximo)}
                        </button>
                      ) : (
                        <span className="text-xs text-neutral-400 italic">Finalizada</span>
                      );
                    })()}
                    <button
                      onClick={() => abrirModal(encomenda.id)}
                      className="text-primary-600 hover:text-blue-800"
                      disabled={encomenda.status !== StatusEncomenda.Aguardando}
                      title={
                        encomenda.status !== StatusEncomenda.Aguardando
                          ? 'Apenas encomendas aguardando podem ser editadas'
                          : 'Editar encomenda'
                      }
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => confirmarRemover(encomenda.id, encomenda.nomeCliente)}
                      className="text-danger-600 hover:text-red-800"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
            disabled={paginaAtual === 1}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="px-4 py-2 text-gray-700">
            Página {paginaAtual} de {totalPaginas}
          </span>
          <button
            onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
            disabled={paginaAtual === totalPaginas}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modal */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {encomendaId ? 'Editar Encomenda' : 'Nova Encomenda'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Seção Cliente */}
              <div className="bg-neutral-50 p-4 rounded-lg border border-gray-200 space-y-4">
                <Select
                  label="Vincular a cliente cadastrado (opcional)"
                  value={formData.clienteId || 0}
                  onChange={(e) => handleClienteChange(e.target.value)}
                  disabled={!!encomendaId}
                >
                  <option value={0}></option>
                  {clientes?.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome} - {cliente.telefone}
                    </option>
                  ))}
                </Select>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Nome do cliente *"
                    type="text"
                    value={formData.nomeCliente}
                    onChange={(e) => setFormData({ ...formData, nomeCliente: e.target.value })}
                    placeholder="Nome do cliente"
                    onValidate={validateRequired('Nome do cliente')}
                    validateOnBlur
                  />
                  <Input
                    label="Contato"
                    type="text"
                    value={formData.contatoCliente}
                    onChange={(e) => setFormData({ ...formData, contatoCliente: aplicarMascaraTelefone(e.target.value) })}
                    placeholder="Telefone ou WhatsApp"
                  />
                </div>
              </div>

              <Input
                label="Data e Hora de Entrega *"
                type="datetime-local"
                value={formData.dataHoraEntrega}
                onChange={(e) => setFormData({ ...formData, dataHoraEntrega: e.target.value })}
                onValidate={combineValidations(
                  validateRequired('Data de entrega'),
                  validateFutureDate
                )}
                validateOnBlur
                helperText="A entrega deve ser agendada para o futuro"
              />

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="retirada"
                    checked={formData.retirada}
                    onChange={(e) => {
                      const retirada = e.target.checked;
                      setFormData({
                        ...formData,
                        retirada,
                        enderecoEntrega: retirada ? '' : formData.enderecoEntrega,
                      });
                    }}
                    className="rounded"
                  />
                  <label htmlFor="retirada" className="text-sm font-medium text-neutral-700 cursor-pointer">
                    Cliente irá retirar
                  </label>
                </label>
              </div>

              {!formData.retirada && (
                <Input
                  label="Endereço de Entrega *"
                  type="text"
                  value={formData.enderecoEntrega}
                  onChange={(e) => setFormData({ ...formData, enderecoEntrega: e.target.value })}
                  placeholder="Rua, número, bairro, cidade"
                  onValidate={validateRequired('Endereço')}
                  validateOnBlur
                />
              )}

              <Input
                label="Desconto (R$)"
                type="text"
                value={formData.desconto}
                onChange={(e) =>
                  setFormData({ ...formData, desconto: e.target.value.replace(/[^0-9.,]/g, '') })
                }
                placeholder="0,00"
                onValidate={validateNonNegativeNumber('Desconto')}
                validateOnBlur
              />

              <Input
                label="Valor de Entrada (R$)"
                type="text"
                value={formData.valorEntrada}
                onChange={(e) =>
                  setFormData({ ...formData, valorEntrada: e.target.value.replace(/[^0-9.,]/g, '') })
                }
                placeholder="0,00"
                onValidate={validateNonNegativeNumber('Valor de entrada')}
                validateOnBlur
              />

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.entradaPaga}
                    onChange={(e) => setFormData({ ...formData, entradaPaga: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Entrada paga</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Observações opcionais"
                />
              </div>

              {/* Produtos */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produtos *
                </label>

                {formData.itens.length > 0 && (
                  <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500">Produto</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500">Preço Unit.</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500">Quantidade</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500">Subtotal</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.itens.map((item: any) => {
                          const produto = produtos?.find((p) => p.id === item.produtoId);
                          if (!produto) return null;
                          return (
                            <tr key={item.produtoId}>
                              <td className="px-4 py-2 text-sm text-gray-900">{produto.nome}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {formatCurrency(produto.precoUnitario)}
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={item.quantidade || ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                    atualizarQuantidade(item.produtoId, parseInt(val) || 1);
                                  }}
                                  className="w-20 border border-gray-300 rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {formatCurrency(produto.precoUnitario * item.quantidade)}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => removerProduto(item.produtoId)}
                                  className="text-danger-600 hover:text-red-800"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-neutral-50 font-semibold">
                          <td colSpan={3} className="px-4 py-2 text-right text-sm text-gray-900">Total:</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(calcularTotal())}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex gap-2">
                  <select
                    id="produtoSelect"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    defaultValue={0}
                  >
                    <option value={0}>Selecione um produto para adicionar</option>
                    {produtos
                      ?.filter((p) => !formData.itens.some((item: any) => item.produtoId === p.id))
                      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                      .map((produto) => (
                        <option key={produto.id} value={produto.id}>
                          {produto.nome} - R$ {produto.precoUnitario.toFixed(2)}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={adicionarProduto}
                    className="bg-success-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    Adicionar
                  </button>
                </div>

                {formData.itens.length === 0 && (
                  <p className="text-sm text-neutral-500 mt-2">
                    Adicione pelo menos um produto à encomenda
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {encomendaId ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmarRemocao.isOpen}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover a encomenda do cliente ${confirmarRemocao.encomendaNome}?`}
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemocao}
        onCancel={fecharConfirmacaoRemocao}
      />

      <ConfirmModal
        isOpen={confirmarStatus.isOpen}
        title="Confirmar Alteração de Status"
        message={`Tem certeza que deseja alterar o status de "${confirmarStatus.statusAtual ? getStatusLabel(confirmarStatus.statusAtual) : ''}" para "${confirmarStatus.novoStatus ? getStatusLabel(confirmarStatus.novoStatus) : ''}"?`}
        confirmText="Alterar"
        cancelText="Cancelar"
        onConfirm={handleConfirmarMudancaStatus}
        onCancel={fecharConfirmacaoStatus}
      />
    </div>
  );
};

export default Encomendas;
